<script lang="ts">
  import { untrack } from 'svelte'
  import { classifyAssistance } from '$lib/public/wizard-state'
  import type { Draft } from '$lib/public/wizard-state'

  interface Props {
    assistance: Draft['assistance']
    species: Draft['animal']['species']
  }
  let { assistance = $bindable(), species }: Props = $props()

  // Restore the answers from an existing classification (back-navigation). These
  // read the props exactly once at mount - untrack makes that one-time intent
  // explicit (the answers are mutable local state thereafter).
  let q1 = $state<'yes' | 'no' | ''>(
    untrack(() => (assistance == null ? '' : assistance.type === 'PET' ? 'no' : 'yes')),
  )
  let q2 = $state<'yes' | 'no' | ''>(
    untrack(() =>
      assistance?.type === 'SERVICE'
        ? 'yes'
        : assistance?.type === 'ESA' && species === 'DOG'
          ? 'no'
          : '',
    ),
  )
  let taskDescription = $state(
    untrack(() => (assistance?.type === 'SERVICE' ? assistance.taskDescription : '')),
  )

  const neededForDisability = $derived(q1 === '' ? null : q1 === 'yes')
  const taskTrainedDog = $derived(q2 === '' ? null : q2 === 'yes')

  // Leaving an upstream answer clears the ones below it, so a hidden stale
  // selection can't silently reappear: Q1 away from "yes" drops the dog/task
  // answers; Q2 away from "yes" drops the task text.
  $effect(() => {
    if (q1 !== 'yes') q2 = ''
    if (q1 !== 'yes' || q2 !== 'yes') taskDescription = ''
  })

  // Recompute the derived classification whenever an answer (or typed task)
  // changes. For SERVICE we splice in the separately-held task text so it isn't
  // lost each recompute.
  $effect(() => {
    const result = classifyAssistance({ neededForDisability, taskTrainedDog }, species)
    if (result?.type === 'SERVICE') {
      assistance = { type: 'SERVICE', requiredForDisability: true, taskDescription }
    } else {
      assistance = result
    }
  })
</script>

<h2 tabindex="-1">About this animal</h2>
<p class="muted">
  Step two of four. Service and assistance animals are not household pets, so we classify them
  separately.
</p>

<fieldset>
  <legend>Is this animal needed because of a disability?</legend>
  <label class="opt">
    <input type="radio" name="q1" value="no" bind:group={q1} />
    <span>No - this is a household pet</span>
  </label>
  <label class="opt">
    <input type="radio" name="q1" value="yes" bind:group={q1} />
    <span>Yes</span>
  </label>
</fieldset>

{#if q1 === 'yes' && species === 'DOG'}
  <fieldset>
    <legend>
      Has your dog been individually trained to perform a specific task or do work related to the
      disability?
    </legend>
    <label class="opt">
      <input type="radio" name="q2" value="yes" bind:group={q2} />
      <span>Yes - trained to perform a task (service animal)</span>
    </label>
    <label class="opt">
      <input type="radio" name="q2" value="no" bind:group={q2} />
      <span>No - provides support but isn't task-trained</span>
    </label>
  </fieldset>
{:else if q1 === 'yes'}
  <p class="note">
    Under the ADA, service animals are dogs. Your animal is classified as an emotional support /
    assistance animal.
  </p>
{/if}

{#if assistance?.type === 'SERVICE'}
  <div class="form-field">
    <label for="task">What work or task is the animal trained to perform?</label>
    <input id="task" type="text" bind:value={taskDescription} maxlength="500" />
  </div>
{/if}

{#if assistance?.type === 'ESA'}
  <p class="note">
    You'll be asked to provide a letter from a licensed health-care provider. We only ask what the
    law allows and never ask about your disability.
  </p>
{/if}

<style>
  h2 {
    margin: 0 0 4px;
    font-size: 1.4rem;
    color: var(--ink-strong);
  }
  h2:focus {
    outline: none;
  }
  .muted {
    margin: 0 0 20px;
    color: var(--muted);
  }
  fieldset {
    border: none;
    margin: 0 0 12px;
    padding: 0;
  }
  legend {
    font-weight: 600;
    color: var(--ink);
    margin-bottom: 8px;
    padding: 0;
  }
  .opt {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 12px;
    border: 1px solid var(--line-strong);
    border-radius: 6px;
    margin-bottom: 8px;
    cursor: pointer;
    font-size: 0.95rem;
    color: var(--ink);
  }
  .opt input {
    margin-top: 3px;
    min-width: 18px;
    min-height: 18px;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .form-field input {
    padding: 10px 12px;
    min-height: 44px;
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 1rem;
  }
  .note {
    margin-top: 4px;
    color: var(--muted);
    font-size: 0.875rem;
  }
</style>
